import React, { useEffect, useState } from "react";
import { FaBars, FaTimes, FaUserCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import "../css/Header.css";
import logo from "../assets/Presentation1.png";
import { useNavigate } from "react-router-dom";
import { handleUnauthorized } from "../utils/api";
import { logout as apiLogout } from "../api/auth";
import { getAccounts, toggleAccountActive, reconnectAccount as apiReconnectAccount } from "../api/accounts";

function Header({ username, setModalOpen, onLogout, sessionDetails, onStopImpersonation, onExitDemo, onDemoLogin }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const impersonating = sessionDetails?.impersonating;
  const impersonatedUsername = sessionDetails?.impersonatedUsername;

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint (session-based)
      await apiLogout();
    } catch (error) {
      console.error("Logout request failed:", error);
      // Continue with logout even if backend request fails
    }
    
    // Use centralized handler to clear all session data
    handleUnauthorized();
    
    // Call onLogout callback from App.js to clear state
    if (onLogout) {
      onLogout();
    }
    
    // Close dropdown
    setShowDropdown(false);
    
    if (impersonating && onStopImpersonation) {
      await onStopImpersonation();
    }

    navigate("/");
  };

  const toggleAccountStatus = async (accountId, currentStatus) => {
    try {
      await toggleAccountActive(accountId);
      setAccounts((prevAccounts) =>
        prevAccounts.map((account) =>
          account.accountId === accountId ? { ...account, enabled: !currentStatus } : account
        )
      );
      setTimeout(() => { window.location.reload(); }, 2000);
    } catch (err) {
      setError("Failed to update account status.");
    }
  };

  const reconnectAccount = async (accountId, brokerageAuthorization) => {
    setModalOpen({ open: true, type: "account" });
    try {
      await apiReconnectAccount(accountId, brokerageAuthorization);
      alert("Reconnect initiated successfully!");
    } catch (error) {
      alert("Failed to reconnect. Please try again.");
    }
  };


  useEffect(() => {
    if (username) {
      const fetchAccounts = async () => {
        try {
          const data = await getAccounts();
          setAccounts(data);
        } catch (err) {
          console.error("Error fetching accounts:", err);
          setError("Failed to load accounts.");
        }
      };
      fetchAccounts();
    }
  }, [username]);

  return (
    <header className="header">
      <div className="content-wrapper">
        {/* Logo */}
        <div className="logo-container">
          <Link to="/">
            <img src={logo} alt="Logo" className="logo" />
          </Link>
        </div>

        {/* Hamburger Menu */}
        <button className="menu-toggle" onClick={toggleMenu}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        {/* Dropdown Menu for Mobile */}
        <nav className={`nav-links ${menuOpen ? "active" : ""}`}>
          {username ? (
            // Logged-in user navigation
            <>
              {/* Show username in hamburger menu on mobile */}
              <div className="mobile-user-info" style={{ 
                padding: '12px 20px', 
                borderBottom: '2px solid rgba(42, 157, 143, 0.2)',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FaUserCircle style={{ fontSize: '1.2rem', color: '#f9f9f9' }} />
                <span style={{ color: '#f9f9f9', fontWeight: 600 }}>{username}</span>
              </div>
              <Link to="/tax-insights" onClick={toggleMenu}>
                Tax Insights
              </Link>
              <Link to="/liquidation-calculator" onClick={toggleMenu}>
                Liquidation Calculator
              </Link>
              <Link to="#" onClick={(e) => { e.preventDefault(); toggleMenu(); }} className="nav-item-disabled">
                RSU
                <span className="coming-soon-tag">Coming Soon</span>
              </Link>
              <Link to="/api/chat" onClick={toggleMenu}>
                <span className="nav-item-title">
                  ProfiTree AI
                  <span className="beta-tag">Beta</span>
                </span>
              </Link>
              {/* Profile, Contact Us, and Logout are in dropdown menu for desktop, but shown here for mobile */}
              <div className="mobile-only-links">
                <Link to="/profile" onClick={toggleMenu}>
                  Profile
                </Link>
                <Link to="/contact-us" onClick={toggleMenu}>
                  Contact Us
                </Link>
                <button 
                  className="btn btn-demo" 
                  onClick={async () => {
                    toggleMenu();
                    await handleLogout();
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(249, 249, 249, 0.3)',
                    color: '#f9f9f9',
                    padding: '12px 20px',
                    width: '100%',
                    textAlign: 'left',
                    borderRadius: '4px',
                    marginTop: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </div>
            </>
          ) : (
            // Not logged-in navigation
            <>
              <Link to="/tax-insights" onClick={toggleMenu}>
                Tax Insights
              </Link>
              <Link 
                to="#" 
                onClick={(e) => {
                  e.preventDefault();
                  toggleMenu();
                  if (onDemoLogin) {
                    onDemoLogin();
                  }
                }}
              >
                <span className="nav-item-title">
                  Demo
                </span>
              </Link>
              {/* <Link to="/pricing" onClick={toggleMenu}>
                Pricing
              </Link> */}
              <Link to="/blog" onClick={toggleMenu}>
                Learning
              </Link>
              <Link to="/about" onClick={toggleMenu}>
                About Us
              </Link>
            </>
          )}
        </nav>
        <div className="header-buttons">
          {username ? ( // Directly check for username prop
            <>
              {/* Exit Demo Mode button - shown when username is "demo" */}
              {username === "demo" && (
                <button 
                  className="btn btn-demo exit-demo-button"
                  onClick={async () => {
                    if (onExitDemo) {
                      await onExitDemo();
                    }
                  }}
                >
                  Exit Demo
                </button>
              )}
              <div className="user-account" onClick={toggleDropdown}>
                <FaUserCircle className="user-icon" />
                <span className="username">{username}</span>
                {showDropdown && (
                  <div className="dropdown-menu">
                    {impersonating && (
                      <div className="impersonation-context">
                        <p>Impersonating {impersonatedUsername}</p>
                        <button
                          className="btn btn-demo impersonation-stop"
                          onClick={async () => {
                            if (onStopImpersonation) {
                              await onStopImpersonation();
                            }
                            setShowDropdown(false);
                          }}
                        >
                          Stop impersonating
                        </button>
                      </div>
                    )}
                    {username === "demo" && (
                      <button 
                        className="btn btn-demo" 
                        onClick={async () => {
                          if (onExitDemo) {
                            await onExitDemo();
                          }
                          setShowDropdown(false);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          width: '100%',
                          marginBottom: '8px'
                        }}
                      >
                        Exit Demo Mode
                      </button>
                    )}
                    <a href="/profile">Profile</a>
                    <a href="/contact-us">Contact Us</a>
                    <a href="/reset-password">Reset Password</a>
                    <button className="btn btn-demo" onClick={handleLogout}>
                      Logout
                    </button>
                  <div className="accounts-seperator"></div>
                  <div className="active-accounts-profile">
                    <div className="accounts-info-header">
                      <p>Account Name</p>
                      <p>Action</p>
                    </div>
                    {accounts.map((account) => (
                      <div className="accounts-info">
                        <p>{account.accountName}</p>
                        {account.snapTradeDisabled ? (
                          <button
                            className="action-button-enable"
                            onClick={() =>
                              reconnectAccount(account.accountId, account.brokerageAuthorization)
                            }
                          >
                            Reconnect
                          </button>
                        ) : account.enabled ? (
                          <button
                            className="status-disable"
                            onClick={() =>
                              toggleAccountStatus(account.accountId, false)
                            }
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            className="action-button-enable"
                            onClick={() =>
                              toggleAccountStatus(account.accountId, true)
                            }
                          >
                            Enable
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </>
          ) : (
            <div className="header-auth-buttons">
              <Link to="/auth">
                <button className="btn btn-demo cta-secondary-button">
                  Log In
                </button>
              </Link>
              <Link to="/signup">
                <button className="btn btn-demo cta-secondary-button cta-button cta-signup">
                  Get Started
                </button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
